import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Zap, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

// One-tap pagamento con carta salvata + sconto cashback opzionale. onCheckout(useCashback) apre Stripe Checkout.
export default function SavedCardPay({ endpoint, amount, label = "Paga", onPaid, onCheckout, checkoutLabel = "Checkout" }) {
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cb, setCb] = useState(null);
  const [useCb, setUseCb] = useState(false);

  useEffect(() => {
    api.get("/payments/methods").then((r) => {
      setMethods(r.data);
      if (r.data.length) setSelected(r.data[0].id);
    }).catch(() => {});
    if (amount > 0) api.get("/cashback/quote", { params: { amount } }).then((r) => setCb(r.data)).catch(() => {});
  }, [amount]);

  const payWithCard = async () => {
    setBusy(true);
    try {
      await api.post(endpoint, { payment_method_id: selected, use_cashback: useCb });
      toast.success("Pagamento completato");
      onPaid?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cashbackToggle = cb && cb.usable > 0 && (
    <label data-testid="cashback-toggle" className="flex items-center justify-between gap-3 rounded-xl border border-[#E0B94B]/60 bg-[#E0B94B]/10 px-3 py-2 text-sm cursor-pointer">
      <span className="flex items-center gap-2"><Wallet size={15} />Usa credito cashback <b data-testid="cashback-usable">€{cb.usable.toFixed(2)}</b></span>
      <span className="flex items-center gap-2 text-xs text-neutral-600">{useCb ? `paghi €${cb.cash.toFixed(2)}` : `su €${cb.balance.toFixed(2)}`}<Switch data-testid="cashback-switch" checked={useCb} onCheckedChange={setUseCb} /></span>
    </label>
  );
  const payLabel = useCb && cb ? `€${cb.cash.toFixed(2)}` : "";

  if (methods.length > 0) {
    const current = methods.find((m) => m.id === selected) || methods[0];
    return (
      <div className="flex flex-col gap-2 w-full">
        {cashbackToggle}
        {methods.length > 1 && (
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="card-select" className="h-11 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {methods.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`card-option-${m.id}`}>
                  <span className="capitalize">{m.brand}</span> •••• {m.last4} · {String(m.exp_month).padStart(2, "0")}/{m.exp_year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button data-testid="pay-saved-card-btn" onClick={payWithCard} disabled={busy || !selected}
          className="h-12 rounded-full bg-black text-white px-6 justify-center">
          <Zap size={17} className="mr-1.5" />{busy ? "Pago…" : `${label} ${payLabel} · •••• ${current.last4}`}
        </Button>
        {onCheckout && (
          <button data-testid="pay-checkout-link" onClick={() => onCheckout(useCb)} className="text-xs text-neutral-500 underline">
            oppure paga con un'altra carta
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2 w-full items-end">
      {cashbackToggle}
      <Button data-testid="pay-checkout-btn" onClick={() => onCheckout?.(useCb)} disabled={!onCheckout}
        className="h-12 rounded-full bg-black text-white px-6">
        <CreditCard size={17} className="mr-1.5" />{checkoutLabel} {payLabel}
      </Button>
    </div>
  );
}
